import { getAccessToken } from "@/providers/auth/token-provider";

const isSuccessfulResponse = (response: Response) => response.status > 199 && response.status < 300;

/**
 * Fetches data from the given URL with the provided options and token.
 *
 * @param url - The URL to fetch.
 * @param options - The options for the fetch request.
 * @returns - The response data.
 * @throws - If the fetch request fails.
 */
export const fetchWithToken = async (url: string, options: RequestInit) => {
  const token = getAccessToken();
  if (!token) {
    throw new Error("One token less");
  }

  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };

  const response = await fetch(url, {
    ...options,
    headers,
    cache: "no-store",
  });

  // Need to throw the response if the status is not in the 200 range so that the error can be caught by other providers like the auth provider.
  if (!isSuccessfulResponse(response)) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    (error as Error & { status: number; statusCode: number }).status = response.status;
    (error as Error & { status: number; statusCode: number }).statusCode = response.status;
    throw error;
  }

  return response.json();
};

/**
 * Fetches all pages of resources from the given initial URL.
 *
 * @param initialUrl - The initial URL to start fetching.
 * @param headers - The headers to include in the fetch request.
 * @param onProgress - Optional callback invoked after each page is fetched. Receives the cumulative entries array (all entries fetched so far, not just the current page).
 * @returns - The response data and total count.
 */
export const fetchAllPages = async (
  initialUrl: string,
  headers: HeadersInit,
  maxEntries?: number,
  onProgress?: (cumulativeEntries: unknown[]) => void
) => {
  const entries: unknown[] = [];
  let url: string | undefined = initialUrl;

  do {
    const response = await fetchWithToken(url, { headers });
    const fetchEntries = response.entry ?? [];
    entries.push(...fetchEntries);

    if (maxEntries && entries.length >= maxEntries) {
      break;
    }
    onProgress?.([...entries]);

    url = response.link?.find((link: { relation: string }) => link.relation === "next")?.url;
  } while (url);

  return { data: entries, total: entries.length };
};

/**
 * Fetches a single page of resources from the given URL.
 *
 * @param url - The URL to fetch.
 * @param headers - The headers to include in the fetch request.
 * @returns - The response data and total count.
 */
export const fetchPage = async (url: string, headers: HeadersInit) => {
  const response = await fetchWithToken(url, { headers });
  return { data: response.entry ?? [], total: response.entry.length ?? 0 };
};
